import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaPlus, FaTrash, FaFilePdf, FaCheck, FaEdit, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { toast } from 'react-toastify';
import offerService from '../services/offerService';
import { generateOfferPDF } from '../utils/pdfGenerator';
import bankService from '../services/bankService';
import api from '../services/api';

const OffersPage = () => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'offer_date', direction: 'desc' });
  const [expandedRow, setExpandedRow] = useState(null);

  const fetchOffers = async () => {
    try {
      const data = await offerService.getAll();
      setOffers(data);
    } catch (error) {
      toast.error('Teklifler yüklenirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOffers();
  }, []);

  const toggleRow = (id) => setExpandedRow(expandedRow === id ? null : id);

  const handleStatusChange = async (id, status) => {
    if (window.confirm(`Teklif durumunu "${status}" olarak değiştirmek istiyor musunuz?`)) {
      try {
        await offerService.updateStatus(id, status);
        toast.success('Durum güncellendi.');
        fetchOffers();
      } catch (error) {
        toast.error('Güncelleme başarısız.');
      }
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Bu teklifi silmek istediğinize emin misiniz?')) {
      try {
        await offerService.delete(id);
        toast.success('Teklif silindi.');
        fetchOffers();
      } catch (error) {
        toast.error('Silme işlemi başarısız.');
      }
    }
  };

  const handleDownloadPDF = async (id) => {
    try {
      toast.info('PDF hazırlanıyor...');
      const [fullOffer, bankAccounts, settingsRes] = await Promise.all([
        offerService.getById(id),
        bankService.getAllAccounts(),
        api.get('/settings')
      ]);
      await generateOfferPDF(fullOffer, bankAccounts, settingsRes.data);
    } catch (error) {
      console.error("PDF Hatası:", error);
      toast.error('PDF oluşturulurken hata oluştu.');
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const filteredOffers = offers.filter(offer => 
    offer.company_name?.toLocaleLowerCase('tr-TR').includes(searchTerm.toLocaleLowerCase('tr-TR'))
  );

  const sortedOffers = [...filteredOffers].sort((a, b) => {
    let aValue = a[sortConfig.key];
    let bValue = b[sortConfig.key];

    if (sortConfig.key === 'total_amount') {
      aValue = parseFloat(aValue);
      bValue = parseFloat(bValue);
    } else if (sortConfig.key === 'offer_date') {
      aValue = new Date(aValue);
      bValue = new Date(bValue);
    }

    if (aValue < bValue) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  if (loading) return <div>Yükleniyor...</div>;

  return (
    <div className="container-fluid mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Teklifler</h2>
        <Link to="/offers/new" className="btn btn-primary">
          <FaPlus className="me-2" /> Yeni Teklif Oluştur
        </Link>
      </div>

      <div className="mb-3">
        <input
          type="text"
          className="form-control"
          placeholder="Müşteri adı ile ara..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="card shadow-sm">
        <div className="card-body">
          <div className="table-responsive d-none d-md-block">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th onClick={() => handleSort('offer_date')} style={{ cursor: 'pointer' }}>
                    Tarih {sortConfig.key === 'offer_date' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                  </th>
                  <th>Müşteri</th>
                  <th onClick={() => handleSort('total_amount')} style={{ cursor: 'pointer' }}>
                    Tutar {sortConfig.key === 'total_amount' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                  </th>
                  <th>Hazırlayan</th>
                  <th>Durum</th>
                  <th>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {sortedOffers.map((offer) => (
                  <tr key={offer.id}>
                    <td>{new Date(offer.offer_date).toLocaleDateString('tr-TR')}</td>
                    <td>{offer.company_name}</td>
                    <td className="fw-bold">
                      {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: (offer.currency === 'TL' ? 'TRY' : offer.currency) || 'TRY' }).format(offer.total_amount)}
                    </td>
                    <td className="small text-muted">{offer.created_by_name || '-'}</td>
                    <td>
                      <span className={`badge ${
                        offer.status === 'onaylandi' ? 'bg-success' : 
                        offer.status === 'reddedildi' ? 'bg-danger' : 
                        offer.status === 'siparis' ? 'bg-primary' : 'bg-warning'
                      }`}>
                        {offer.status.toUpperCase()}
                      </span>
                    </td>
                    <td>
                      <Link to={`/offers/edit/${offer.id}`} className="btn btn-sm btn-outline-primary me-2" title="Düzenle">
                        <FaEdit />
                      </Link>

                      {/* Detay/Görüntüle Butonu (İleride PDF burada olacak) */}
                      <button onClick={() => handleDownloadPDF(offer.id)} className="btn btn-sm btn-outline-secondary me-2" title="PDF İndir">
                        <FaFilePdf />
                      </button>

                      {/* Durum Değiştirme Butonları */}
                      {offer.status === 'taslak' && (
                        <button onClick={() => handleStatusChange(offer.id, 'onaylandi')} className="btn btn-sm btn-outline-success me-2" title="Onayla">
                          <FaCheck />
                        </button>
                      )}
                      
                      <button onClick={() => handleDelete(offer.id)} className="btn btn-sm btn-outline-danger" title="Sil">
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))}
                {sortedOffers.length === 0 && (
                  <tr>
                    <td colSpan="5" className="text-center">Kayıtlı teklif bulunamadı.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobil Görünüm */}
          <div className="d-md-none">
            {sortedOffers.map((offer) => (
              <div className="card mb-3 shadow-sm" key={offer.id}>
                <div className="card-body" onClick={() => toggleRow(offer.id)} style={{ cursor: 'pointer' }}>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="mb-0 fw-bold">{offer.company_name}</h6>
                      <small className="text-muted">{new Date(offer.offer_date).toLocaleDateString('tr-TR')}</small>
                    </div>
                    <div className="text-end">
                      <div className="fw-bold">{new Intl.NumberFormat('tr-TR', { style: 'currency', currency: (offer.currency === 'TL' ? 'TRY' : offer.currency) || 'TRY' }).format(offer.total_amount)}</div>
                      <span className={`badge ${
                        offer.status === 'onaylandi' ? 'bg-success' : 
                        offer.status === 'reddedildi' ? 'bg-danger' : 
                        offer.status === 'siparis' ? 'bg-primary' : 'bg-warning'
                      }`}>
                        {offer.status.toUpperCase()}
                      </span>
                      <div className="text-muted mt-1">{expandedRow === offer.id ? <FaChevronUp /> : <FaChevronDown />}</div>
                    </div>
                  </div>

                  {expandedRow === offer.id && (
                    <div className="mt-3 border-top pt-3">
                      <div className="d-flex justify-content-end gap-2">
                        <Link to={`/offers/edit/${offer.id}`} className="btn btn-sm btn-outline-primary">
                          <FaEdit />
                        </Link>

                        <button onClick={(e) => { e.stopPropagation(); handleDownloadPDF(offer.id); }} className="btn btn-sm btn-outline-secondary">
                          <FaFilePdf />
                        </button>

                        {offer.status === 'taslak' && (
                          <button onClick={(e) => { e.stopPropagation(); handleStatusChange(offer.id, 'onaylandi'); }} className="btn btn-sm btn-outline-success">
                            <FaCheck />
                          </button>
                        )}
                        
                        <button onClick={(e) => { e.stopPropagation(); handleDelete(offer.id); }} className="btn btn-sm btn-outline-danger">
                          <FaTrash />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OffersPage;